import React, { useState } from "react";
import { Network } from "lucide-react";
import useFireboxLive, { saKey, wib, rates, mbps } from "./useFireboxLive";
import { IFACES, GWS, VIFS } from "./WgSystemManagerJakarta";

// Network topology diagram of the Jakarta Firebox T80 (NFPIMIDJKTKN01): uplinks, internal segments and BOVPN gateways.
// Layout is fixed; interface status, throughput and tunnel flags are overlaid live from GET /api/net/firebox/status.

const PORT_C = { 0: "#dc2626", 1: "#15803d", 2: "#f59e0b", 3: "#7e22ce", 4: "#64748b", 5: "#0f766e", 6: "#1d4ed8", 7: "#be185d" };
const TINT = { 0: "#fde2e2", 1: "#dcfce7", 2: "#fef3c7", 3: "#f3e8ff", 5: "#ccfbf1", 6: "#dbeafe", 7: "#fce7f3" };
const OK = "#16a34a", DOWN = "#dc2626", OFF = "#94a3b8";
const MONO = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

// Uplinks drawn above the device, segments drawn below it. Text describes the far side of each link.
const TOP = [
  { n: 0, cx: 170, title: "ISP Velo", sub: "Primary internet" },
  { n: 2, cx: 548, title: "ISP Neuviz", sub: "Secondary internet" },
  { n: 6, cx: 830, title: "AWS Singapore (PIL)", sub: "via Velo MikroTik" },
];
const BOTTOM = [
  { n: 1, cx: 130, lane: 490, title: "Management Network", lines: ["Firebox management", "10.0.1.1"] },
  { n: 3, cx: 375, lane: 505, title: "AWS-JKT Network", lines: ["AWS Jakarta", "(via ISP / tunnel)"] },
  { n: 5, cx: 620, lane: 520, title: "Internal Network (PIM)", lines: ["Core switch", "Servers / users 10.32.0.x"] },
  { n: 7, cx: 865, lane: 535, title: "SAP Network", lines: ["SAP host testing", "segment"] },
];
const PORT_X = (n) => 420 + n * 64;
const DEV = { x: 270, y: 390, w: 660, h: 70 };
const VIF_PEER = ["16.78.66.179", "108.136.96.201"];

const toInt = (ip) => String(ip).split(".").reduce((a, o) => a * 256 + Number(o), 0);
const toIp = (v) => [24, 16, 8, 0].map((s) => Math.floor(v / Math.pow(2, s)) % 256).join(".");
const bits = (mask) => String(mask).split(".").reduce((a, o) => a + Number(o).toString(2).split("").filter((c) => c === "1").length, 0);
function cidr(ip, mask) {
  if (!ip || !mask) return ip || "";
  const b = bits(mask);
  const size = Math.pow(2, 32 - b);
  return toIp(Math.floor(toInt(ip) / size) * size) + "/" + b;
}
const status = (f) => (f.zone === "Disabled" ? "off" : f.oper === "down" ? "down" : "up");
const statusC = (s) => (s === "up" ? OK : s === "down" ? DOWN : OFF);

function Cloud({ cx, color, tint, title, sub, net }) {
  return (
    <g>
      <path transform={"translate(" + (cx - 135) + ",30) scale(2)"} vectorEffect="non-scaling-stroke"
        d="M25,60 a20,20 0 0,1 0,-40 a25,25 0 0,1 45,-10 a22,22 0 0,1 40,10 a20,20 0 0,1 0,40 z"
        fill={tint} stroke={color} strokeWidth="2" />
      <text x={cx} y={88} textAnchor="middle" fontSize="17" fontWeight="700" fill="#0f172a">{title}</text>
      <text x={cx} y={108} textAnchor="middle" fontSize="13" fill="#334155">{sub}</text>
      <text x={cx} y={127} textAnchor="middle" fontSize="13" fontWeight="600" fill="#0f172a" fontFamily={MONO}>{net}</text>
    </g>
  );
}

function IfBox({ f, cx, y, w = 230 }) {
  const c = PORT_C[f.n];
  const s = status(f);
  const rate = f.rate && (f.rate.inMbps != null || f.rate.outMbps != null) ? "↑ " + mbps(f.rate.outMbps) + "  ↓ " + mbps(f.rate.inMbps) : null;
  const rows = [f.name, f.ip ? f.ip + "/" + bits(f.mask) : "no address"];
  if (f.gw) rows.push("GW: " + f.gw);
  if (rate) rows.push(rate);
  const h = 32 + rows.length * 19 + 8;
  return (
    <g>
      <title>{"eth" + f.n + " " + f.name + " - " + f.zone + (s === "down" ? " (link down)" : "")}</title>
      <rect x={cx - w / 2} y={y} width={w} height={h} rx="8" fill="#fff" stroke={c} strokeWidth="2" />
      <path d={"M" + (cx - w / 2) + "," + (y + 28) + " v-20 a8,8 0 0,1 8,-8 h" + (w - 16) + " a8,8 0 0,1 8,8 v20 z"} fill={c} />
      <text x={cx} y={y + 19} textAnchor="middle" fontSize="13.5" fontWeight="700" fill="#fff">{"eth" + f.n + " - " + f.zone}</text>
      {rows.map((r, i) => (
        <text key={i} x={cx} y={y + 48 + i * 19} textAnchor="middle" fontSize={i ? 13 : 14} fontWeight={i ? 400 : 700} fill={i === 3 ? "#475569" : "#0f172a"} fontFamily={i ? MONO : undefined}>{r}</text>
      ))}
      <circle cx={cx + w / 2 - 12} cy={y + 14} r="5" fill={statusC(s)} stroke="#fff" strokeWidth="1.5" />
    </g>
  );
}

function Device({ IF }) {
  return (
    <g>
      <rect x={DEV.x} y={DEV.y} width={DEV.w} height={DEV.h} rx="6" fill="#1f2937" stroke="#0f172a" />
      <rect x={DEV.x} y={DEV.y} width="110" height={DEV.h} rx="6" fill="#dc2626" />
      <text x={DEV.x + 55} y={DEV.y + 30} textAnchor="middle" fontSize="13" fontWeight="700" fill="#fff">WatchGuard</text>
      <text x={DEV.x + 55} y={DEV.y + 52} textAnchor="middle" fontSize="20" fontWeight="800" fill="#fff">T80</text>
      {IF.map((f) => {
        const x = PORT_X(f.n);
        const s = status(f);
        return (
          <g key={f.n}>
            <title>{"eth" + f.n + ": " + f.name + " [" + f.zone + (s === "down" ? ":Down" : "") + "]"}</title>
            <text x={x} y={DEV.y + 16} textAnchor="middle" fontSize="11" fill="#e2e8f0">{"eth" + f.n}</text>
            <rect x={x - 19} y={DEV.y + 23} width="38" height="28" rx="3" fill="#0b1220" stroke={s === "off" ? "#64748b" : PORT_C[f.n]} strokeWidth="3" />
            {s !== "off" ? <rect className={s === "up" ? "wgt-led" : undefined} style={{ animationDelay: f.n * 0.17 + "s" }} x={x - 15} y={DEV.y + 27} width="9" height="5" rx="1" fill={s === "up" ? "#22c55e" : DOWN} /> : null}
            <circle className={s === "up" ? "wgt-led" : undefined} style={{ animationDelay: f.n * 0.17 + "s" }} cx={x} cy={DEV.y + 61} r="3" fill={statusC(s)} />
          </g>
        );
      })}
    </g>
  );
}

const Dot = ({ x, y, s }) => <circle cx={x} cy={y} r="7" fill={statusC(s)} stroke="#fff" strokeWidth="2" />;

// Animations: blinking LEDs on live ports, flowing dashes on live links, static faded dashes on dead links.
const ANIM_CSS = ".wgt-led{animation:wgtBlink 1.4s ease-in-out infinite}@keyframes wgtBlink{0%,100%{opacity:1}50%{opacity:.2}}" +
  ".wgt-flow{stroke-dasharray:6 14;animation:wgtFlow 1s linear infinite}@keyframes wgtFlow{to{stroke-dashoffset:-20}}" +
  ".wgt-vpn{animation:wgtVpn .9s linear infinite}@keyframes wgtVpn{to{stroke-dashoffset:-12}}" +
  ".wgt-dead{opacity:.6}" +
  "@media (prefers-reduced-motion: reduce){.wgt-led,.wgt-flow,.wgt-vpn{animation:none}}";

function Link({ d, color, s, w = 2.5 }) {
  if (s === "up") {
    return (
      <g>
        <path d={d} stroke={color} strokeWidth={w} fill="none" />
        <path className="wgt-flow" d={d} stroke="#fff" strokeOpacity="0.9" strokeWidth={w - 0.8} strokeLinecap="round" fill="none" />
      </g>
    );
  }
  return <path className="wgt-dead" d={d} stroke={s === "down" ? DOWN : OFF} strokeWidth={w} strokeDasharray="4 6" fill="none" />;
}

export default function WgTopologyJakarta() {
  const { live, prev, checkedAt, error } = useFireboxLive();
  const [hideInactive, setHideInactive] = useState(false);
  const RT = rates(live, prev);
  const keys = new Set(((live && live.sas) || []).map((x) => saKey(x.local, x.remote)));
  const IF = IFACES.map((f) => {
    const L = live && live.ifs && live.ifs.find((x) => x.eth === f.n);
    if (!L) return f;
    return { ...f, zone: L.admin === "down" ? "Disabled" : f.zone, oper: L.oper, rate: RT[f.n] };
  });
  const byN = (n) => IF.find((f) => f.n === n) || { n, name: "eth" + n, zone: "Disabled" };
  const G = live && live.sas ? GWS.map((g) => ({ ...g, t: g.t.map((x) => [x[0], x[1], keys.has(saKey(x[0], x[1])) ? 1 : 0]) })) : GWS;
  const vifUp = (i) => !live || !live.sas || live.sas.some((x) => x.peer === VIF_PEER[i]);
  const totalAct = G.reduce((a, g) => a + g.t.filter((x) => x[2]).length, 0);
  const inUse = IF.filter((f) => f.zone !== "Disabled");
  const upCount = inUse.filter((f) => status(f) === "up").length;

  // Right-hand BOVPN panel: card heights depend on the number of tunnel rows shown.
  const PX = 1040, PW = 450, ROW = 18;
  let cy = 58;
  const cards = G.map((g) => {
    const rows = hideInactive ? g.t.filter((x) => x[2]) : g.t;
    const act = g.t.filter((x) => x[2]).length;
    const h = 56 + rows.length * ROW + 10;
    const c = { g, rows, act, y: cy, h };
    cy += h + 14;
    return c;
  });
  const vifCard = { y: cy, h: 56 + VIFS.length * ROW + 10 };
  cy += vifCard.h + 10;
  const H = Math.max(830, cy + 10);

  return (
    <div style={{ background: "var(--card-bg, #fff)", border: "1px solid #e2e8f0", borderRadius: 12, padding: 16 }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 8 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, fontSize: 16 }}>
            <Network size={18} /> Network Topology — NFPIMIDJKTKN01
          </div>
          <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
            WatchGuard T80 · Fireware {(live && live.firmware) || "12.12.2.B745592"} · {upCount}/{inUse.length} in-use interfaces up · {totalAct} BOVPN tunnels active
            {checkedAt ? " · live SNMP, checked " + wib(checkedAt) : " · static snapshot (no live data)"}
            {error ? " · last poll error: " + error : ""}
          </div>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 14, fontSize: 12, color: "#475569" }}>
          <span><b style={{ color: OK }}>●</b> Up</span>
          <span><b style={{ color: DOWN }}>●</b> Down</span>
          <span><b style={{ color: OFF }}>●</b> Disabled / inactive</span>
          <span><span style={{ display: "inline-block", width: 22, borderTop: "2px dashed " + OK, verticalAlign: "middle", marginRight: 4 }} />BOVPN</span>
          <label style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
            <input type="checkbox" checked={hideInactive} onChange={(e) => setHideInactive(e.target.checked)} /> Hide inactive tunnels
          </label>
        </div>
      </div>

      <div style={{ overflowX: "auto" }}>
        <svg viewBox={"0 0 1500 " + H} style={{ width: "100%", minWidth: 900, height: "auto", display: "block" }} fontFamily="Inter, system-ui, sans-serif">
          <style>{ANIM_CSS}</style>
          {/* Uplinks */}
          {TOP.map((t) => {
            const f = byN(t.n);
            const s = status(f);
            const px = PORT_X(t.n);
            return (
              <g key={t.n}>
                <Link d={"M" + t.cx + ",150 V200"} color={PORT_C[t.n]} s={s} />
                <Link d={"M" + t.cx + ",250 V345 H" + px + " V" + (DEV.y + 23)} color={PORT_C[t.n]} s={s} />
                <Cloud cx={t.cx} color={PORT_C[t.n]} tint={TINT[t.n]} title={t.title} sub={t.n === 6 ? t.sub + " " + (f.gw || "") : t.sub} net={cidr(f.ip, f.mask)} />
                <Dot x={t.cx} y={175} s={s} />
                <IfBox f={f} cx={t.cx} y={200} />
              </g>
            );
          })}

          <Device IF={IF} />
          <text x={DEV.x - 12} y={DEV.y + 22} textAnchor="end" fontSize="15" fontWeight="700" fill="#0f172a">NFPIMIDJKTKN01</text>
          <text x={DEV.x - 12} y={DEV.y + 40} textAnchor="end" fontSize="12" fill="#475569">WatchGuard T80 · Jakarta</text>
          <text x={DEV.x - 12} y={DEV.y + 57} textAnchor="end" fontSize="12" fill="#475569">LAN 10.32.0.17</text>

          {/* Internal / external segments */}
          {BOTTOM.map((b) => {
            const f = byN(b.n);
            const s = status(f);
            const px = PORT_X(b.n);
            const c = PORT_C[b.n];
            return (
              <g key={b.n}>
                <Link d={"M" + px + "," + (DEV.y + DEV.h) + " V" + b.lane + " H" + b.cx + " V560"} color={c} s={s} />
                <Dot x={px} y={DEV.y + DEV.h + 14} s={s} />
                <Link d={"M" + b.cx + ",600 V690"} color={c} s={s} w={2} />
                <IfBox f={f} cx={b.cx} y={560} w={225} />
                <rect x={b.cx - 112} y={690} width="224" height="118" rx="10" fill={TINT[b.n]} fillOpacity="0.45" stroke={c} strokeDasharray="6 4" />
                <text x={b.cx} y={715} textAnchor="middle" fontSize="14" fontWeight="700" fill={c}>{b.title}</text>
                <text x={b.cx} y={735} textAnchor="middle" fontSize="13" fill="#0f172a" fontFamily={MONO}>{cidr(f.ip, f.mask)}</text>
                {b.lines.map((l, i) => (
                  <text key={i} x={b.cx} y={765 + i * 19} textAnchor="middle" fontSize="13" fill="#334155">{l}</text>
                ))}
              </g>
            );
          })}

          {/* Disabled port note */}
          {IF.filter((f) => f.zone === "Disabled").map((f) => (
            <text key={f.n} x={PORT_X(f.n)} y={DEV.y - 10} textAnchor="middle" fontSize="11" fill={OFF}>{"eth" + f.n + " disabled"}</text>
          ))}

          {/* BOVPN panel */}
          <rect x={PX - 10} y={14} width={PW + 20} height={H - 24} rx="12" fill="#f0fdfa" fillOpacity="0.5" stroke="#5eead4" strokeDasharray="6 4" />
          <text x={PX} y={42} fontSize="16" fontWeight="700" fill="#0f766e">Branch Office VPN Tunnels</text>
          {cards.map((c, k) => {
            const up = c.act > 0;
            const col = up ? OK : DOWN;
            const sy = DEV.y + 8 + k * 14;
            const vx = 980 + k * 12;
            const ty = c.y + 24;
            return (
              <g key={c.g.name}>
                <path className={up ? "wgt-vpn" : "wgt-dead"} d={"M" + (DEV.x + DEV.w) + "," + sy + " H" + vx + " V" + ty + " H" + PX} stroke={col} strokeWidth="2" strokeDasharray="7 5" fill="none" />
                <rect x={PX} y={c.y} width={PW} height={c.h} rx="10" fill="#fff" stroke="#cbd5e1" />
                <Dot x={PX} y={ty} s={up ? "up" : "down"} />
                <text x={PX + 18} y={c.y + 24} fontSize="14.5" fontWeight="700" fill="#0f172a">{c.g.name.replace("To", "to") + " (IKEv1)"}</text>
                <rect x={PX + PW - 62} y={c.y + 10} width="50" height="20" rx="5" fill={up ? "#dcfce7" : "#fee2e2"} stroke={col} />
                <text x={PX + PW - 37} y={c.y + 24} textAnchor="middle" fontSize="11.5" fontWeight="700" fill={col}>{up ? "UP" : "DOWN"}</text>
                <text x={PX + 18} y={c.y + 43} fontSize="12.5" fill="#475569">{c.act + " of " + c.g.t.length + " tunnels active"}</text>
                {c.rows.map((r, i) => (
                  <text key={i} x={PX + 18} y={c.y + 64 + i * ROW} fontSize="12" fontFamily={MONO} fill={r[2] ? "#0f172a" : "#94a3b8"}>
                    {(r[2] ? "● " : "○ ") + r[0] + "  →  " + r[1] + (r[2] ? "" : "  (inactive)")}
                  </text>
                ))}
              </g>
            );
          })}
          {(() => {
            const nUp = VIFS.filter((_, i) => vifUp(i)).length;
            const col = nUp === VIFS.length ? OK : nUp ? "#f59e0b" : DOWN;
            const ty = vifCard.y + 24;
            const vx = 980 + cards.length * 12;
            return (
              <g>
                <path className={nUp ? "wgt-vpn" : "wgt-dead"} d={"M" + (DEV.x + DEV.w) + "," + (DEV.y + 8 + cards.length * 14) + " H" + vx + " V" + ty + " H" + PX} stroke={col} strokeWidth="2" strokeDasharray="7 5" fill="none" />
                <rect x={PX} y={vifCard.y} width={PW} height={vifCard.h} rx="10" fill="#fff" stroke="#cbd5e1" />
                <circle cx={PX} cy={ty} r="7" fill={col} stroke="#fff" strokeWidth="2" />
                <text x={PX + 18} y={vifCard.y + 24} fontSize="14.5" fontWeight="700" fill="#0f172a">AWS Jakarta VPN (route-based)</text>
                <text x={PX + 18} y={vifCard.y + 43} fontSize="12.5" fill="#475569">{nUp + " of " + VIFS.length + " tunnels up"}</text>
                {VIFS.map((v, i) => (
                  <text key={i} x={PX + 18} y={vifCard.y + 64 + i * ROW} fontSize="12" fontFamily={MONO} fill={vifUp(i) ? "#0f172a" : "#94a3b8"}>
                    {(vifUp(i) ? "● " : "○ ") + v.replace("VPN Interface ", "").replace(" [IKEv1]", "") + " → " + VIF_PEER[i] + (vifUp(i) ? "" : "  (down)")}
                  </text>
                ))}
              </g>
            );
          })()}
        </svg>
      </div>
    </div>
  );
}
